/**
 * ClientIntro: the pre-call setup screen shown to the respondent.
 *
 * Renders either a 2-column video layout (when videoEnabled) or a centered
 * single-column audio-only layout. Delegates device setup to MediaSetup or
 * MicrophoneSetup, and language selection to LanguageSelector.
 *
 * @module components/ClientIntro
 */

import React, { useCallback, useRef, useState } from 'react';
import MicrophoneSetup from './MicrophoneSetup';
import MediaSetup from './MediaSetup';
import LanguageSelector from './LanguageSelector';

/** Props for the ClientIntro component. */
export interface ClientIntroProps {
  /** Whether the session uses video. Controls layout and device setup component. */
  videoEnabled: boolean;
  /** Full name of the interviewee; first word is used in the heading. */
  intervieweeName?: string;
  /** Short duration estimate phrase, e.g. "~8 min". Null hides the estimate. */
  durationLabel: string | null;
  /** BCP-47 language code for the interview. */
  interviewLanguage: string;
  /** Non-null when the host has locked the language. */
  effectiveEnforceLanguage: string | null;
  /** Whether the language selector should be disabled. */
  isLanguageLocked: boolean;
  /** Whether the interview has already started (locks language). */
  interviewHasStarted: boolean;
  /** Whether a call start is in progress. */
  startingCall: boolean;
  /** Error message from a failed start attempt, if any. */
  startCallError: string | null;
  /** Whether the host requires camera to be on. */
  effectiveRequireVideo: boolean;
  /** Called when the user selects a language. */
  onLanguageChange: (lang: string) => void;
  /** Called when the user selects a microphone device. */
  onMicrophoneSelect: (id: string) => void;
  /** Called when the user selects a speaker device. */
  onSpeakerSelect: (id: string) => void;
  /** Called when the user selects a camera device. */
  onCameraSelect: (id: string) => void;
  /** Called when the user toggles the camera on/off. */
  onCameraToggle: (enabled: boolean) => void;
  /** Called when the user clicks the Start button. */
  onStartCall: () => void;
  /** Optional on-screen alternative, available before any device permission. */
  onAnswerInText?: () => void;
}

/**
 * Pre-call setup screen for the respondent.
 *
 * Renders device setup controls (camera+mic or mic-only), a language selector,
 * and the Start button. Layout switches between a 2-column video mode and a
 * centered single-column audio-only mode based on `videoEnabled`.
 */
export default function ClientIntro({
  videoEnabled,
  intervieweeName,
  durationLabel,
  interviewLanguage,
  effectiveEnforceLanguage,
  isLanguageLocked,
  startingCall,
  startCallError,
  effectiveRequireVideo,
  onLanguageChange,
  onMicrophoneSelect,
  onSpeakerSelect,
  onCameraSelect,
  onCameraToggle,
  onStartCall,
  onAnswerInText,
}: ClientIntroProps) {
  // Only surface a note when the language is locked. In the normal case the
  // flag pill speaks for itself, so we keep the screen free of helper copy.
  const lockedNote = !isLanguageLocked
    ? null
    : effectiveEnforceLanguage
      ? 'The host has set the conversation language.'
      : 'Language is locked while in progress.';

  // Name the modality (voice vs video) so it is clear what they are joining,
  // then append the short duration estimate ("It takes ~8 min.") when known.
  const introCopy = `You're invited to join a short AI-guided ${videoEnabled ? 'video' : 'voice'} conversation.`;
  // Permission is deferred to the join click, so the lobby shows no live
  // preview: set the expectation up front (familiar Meet/Teams framing) so a
  // first-time guest is not confused that their camera/mic appears off. Optional
  // video carries its choice in the explicit "Or join without video" button, so
  // it needs no reassurance line.
  const optionalVideo = videoEnabled && !effectiveRequireVideo;
  const readyLine = !videoEnabled
    ? 'Your mic turns on when you join.'
    : effectiveRequireVideo
      ? 'Your camera and mic turn on when you join.'
      : null;
  // Familiar, action-clear button label that also names the modality.
  const joinLabel = videoEnabled ? 'Join video call' : 'Join voice call';

  // Permission is requested on the Start gesture (not on landing) to remove
  // on-arrival friction. The setup component registers its access requester
  // here; clicking Start prompts and only joins once access is granted. The
  // `skipVideo` option drives the audio-only "Or join without video" path.
  const requestAccessRef = useRef<((opts?: { skipVideo?: boolean }) => Promise<boolean>) | null>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);

  const handleAccessRequesterReady = useCallback(
    (request: (opts?: { skipVideo?: boolean }) => Promise<boolean>) => {
      requestAccessRef.current = request;
    },
    [],
  );

  const handleStart = useCallback(async (opts?: { skipVideo?: boolean }) => {
    const requester = requestAccessRef.current;
    if (requester) {
      setRequestingAccess(true);
      try {
        const granted = await requester(opts);
        // Denied/dismissed: the setup component now shows its own retry UI.
        if (!granted) return;
      } finally {
        setRequestingAccess(false);
      }
    }
    onStartCall();
  }, [onStartCall]);

  const startDisabled = startingCall || requestingAccess;

  return (
    /* Centered single-column layout for both audio and video conversations.
       The camera preview (video sessions) lives inside MediaSetup's Test
       devices modal, so the lobby itself stays minimal in both modes. */
    <div className="flex flex-col items-center justify-center h-full text-center px-4 py-8 md:p-8 max-w-xl mx-auto">
      <div className="animated-border-wrapper w-full">
        {/* Extra bottom padding gives the lower-corner utility row (language
            pill + device-test controls) breathing room below the join area. */}
        <div className="glass-card relative p-8 md:p-12 lg:p-16 pb-12 md:pb-16 lg:pb-20">
          {intervieweeName && (
            <h1 className="text-3xl font-bold text-foreground mb-4">
              Ready {intervieweeName.split(' ')[0]}?
            </h1>
          )}
          <p className="text-lg text-muted-foreground mb-2">
            {introCopy}{durationLabel ? ` It takes ${durationLabel}.` : ''}
          </p>
          {readyLine && <p className="text-sm text-subtle-foreground mb-2">{readyLine}</p>}

          {/* Primary join call-to-action: a full-width button. The language
              selector is not inline (it would squash the button on mobile); it
              lives as a quiet pill pinned to the card's lower-left, opposite the
              device-test controls in the lower-right. */}
          <button
            onClick={() => { void handleStart(); }}
            disabled={startDisabled}
            className="mt-6 w-full px-8 py-4 button-accent font-bold text-lg rounded-lg transition-all transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-border disabled:bg-surface-raised disabled:text-muted-foreground disabled:shadow-none disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-surface-hover"
          >
            {joinLabel}
          </button>
          {optionalVideo && (
            <button
              type="button"
              onClick={() => { void handleStart({ skipVideo: true }); }}
              disabled={startDisabled}
              className="mt-2 text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Or join without video
            </button>
          )}
          {onAnswerInText ? (
            <button type="button" onClick={onAnswerInText} disabled={startingCall}
              className="mt-4 text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 cursor-pointer disabled:opacity-50">
              Answer in text
            </button>
          ) : null}

          {/* Device setup is a quiet utility: it pins a settings cog to the
              card's lower corner (opens the test panel) and renders the
              permission/error states here, below the join decision. */}
          <div className="mt-4">
            {videoEnabled ? (
              <MediaSetup
                videoEnabled={true}
                requireVideo={effectiveRequireVideo}
                deferPermission
                onAccessRequesterReady={handleAccessRequesterReady}
                onMicrophoneSelect={onMicrophoneSelect}
                onSpeakerSelect={onSpeakerSelect}
                onCameraSelect={onCameraSelect}
                onCameraToggle={onCameraToggle}
              />
            ) : (
              <MicrophoneSetup
                onDeviceSelect={onMicrophoneSelect}
                onSpeakerSelect={onSpeakerSelect}
                deferPermission
                onAccessRequesterReady={handleAccessRequesterReady}
              />
            )}
          </div>
          {lockedNote && (
            <p className="mt-2 text-xs text-subtle-foreground text-center">{lockedNote}</p>
          )}
          {startCallError && (
            <p className="mt-3 text-xs text-danger text-center">{startCallError}</p>
          )}

          {/* Language is a quiet setup utility, pinned to the card's lower-left
              corner opposite the device-test controls (lower-right). */}
          <LanguageSelector
            variant="compact"
            value={interviewLanguage}
            onChange={onLanguageChange}
            label="Speaking language"
            disabled={isLanguageLocked}
            className="absolute bottom-4 left-4"
          />
        </div>
      </div>
    </div>
  );
}
